import { DragEvent, useEffect, useRef, useState } from 'react';
import { AlertCircle, FileText, RefreshCw, Upload } from 'lucide-react';

export const PDF_ACCEPT = '.pdf,application/pdf';
export const SUA_ACCEPT = '.pdf,.sua,application/pdf,application/octet-stream';

/** Same rule the upload handler enforces: only the "Archivo .SUA" requirement accepts SUA files. */
export const acceptsSuaFiles = (documentName: string) =>
  documentName.toLowerCase().includes('archivo') && documentName.toLowerCase().includes('.sua');

interface UploadDropzoneProps {
  /** Accessible name of the file input, e.g. "Subir Contrato". */
  inputLabel: string;
  buttonText: string;
  acceptsSua: boolean;
  uploading: boolean;
  error?: string | null;
  onFile: (file: File) => void;
}

/**
 * Click or drag-and-drop file picker. It does not validate files itself: the
 * caller's upload handler owns size/type/name rules and reports back `error`.
 */
export default function UploadDropzone({ inputLabel, buttonText, acceptsSua, uploading, error, onFile }: UploadDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const depth = useRef(0);
  const errorId = useRef(`upload-error-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    if (!uploading && !error) setFileName(null);
  }, [uploading, error]);

  const pick = (file: File | undefined) => {
    if (!file || uploading) return;
    setFileName(file.name);
    onFile(file);
  };

  const onDragEnter = (event: DragEvent) => {
    event.preventDefault();
    depth.current += 1;
    if (!uploading) setDragging(true);
  };
  const onDragLeave = (event: DragEvent) => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    pick(event.dataTransfer?.files?.[0]);
  };

  const hint = `${acceptsSua ? 'PDF o SUA' : 'PDF'} · Máx. 50 MB`;

  return (
    <div className="min-w-0 space-y-1.5">
      <label
        onDragEnter={onDragEnter}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`relative flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed px-3 py-2.5 text-center text-sm transition-colors focus-within:ring-2 focus-within:ring-blue-500 ${
          dragging
            ? 'border-blue-500 bg-blue-50 text-blue-800'
            : error
              ? 'border-red-300 bg-white text-slate-700 hover:bg-slate-50'
              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
        } ${uploading ? 'cursor-wait opacity-80' : ''}`}
      >
        <span className="inline-flex items-center gap-2 font-medium">
          {uploading ? <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />}
          {uploading ? 'Subiendo…' : dragging ? 'Suelta el archivo aquí' : buttonText}
        </span>
        {!uploading && <span className="hidden text-xs text-slate-500 sm:inline">o arrástralo aquí</span>}
        <span className="text-xs text-slate-500">{hint}</span>
        <input
          type="file"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label={inputLabel}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? true : undefined}
          accept={acceptsSua ? SUA_ACCEPT : PDF_ACCEPT}
          disabled={uploading}
          onChange={(event) => {
            pick(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </label>
      {fileName && (
        <p className="flex min-w-0 items-center gap-1.5 text-xs text-slate-600" title={fileName}>
          <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{fileName}</span>
        </p>
      )}
      {uploading && (
        <div role="progressbar" aria-label={`Subiendo ${fileName || 'archivo'}`} className="h-1 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-600" />
        </div>
      )}
      {error && !uploading && (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-xs text-red-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
